import React from 'react';
import { ModalHeader } from '../../../modals/ModalHeader';
import { ContentTitle } from '../ContentTitle';
import useModal from '@/hooks/useModal';
import { useQuery } from '@tanstack/react-query';
import { actionRequiredService, GET_RETAINER_DATA } from '@/api';
import { LoaderScreen } from '@/components/common';
import PdfViewer from '@/components/common/pdf-viewer/PdfViewer';
import { DocumentInformationSection } from '../modal-review-sign/DocumentInformationSection';
import { PdfViewerDownloadControl } from '../modal-review-sign/PdfViewerControls';
import { HttpResponse, SignDocumentDetails } from '@/interfaces';

interface Props {
	isOpen: boolean;
	setIsModalOpen: (value: boolean) => void;
	actionRequiredId: number | undefined;
}

export const ModalReviewRetainer: React.FC<Props> = ({
	isOpen,
	setIsModalOpen,
	actionRequiredId,
}) => {
	const { Modal, show, hide } = useModal();

	React.useEffect(() => {
		if (isOpen) show();
	}, [isOpen]);

	const { data: retainer, isLoading } = useQuery({
		queryKey: [GET_RETAINER_DATA],
		queryFn: () =>
			actionRequiredService.getRetainerData(actionRequiredId as number),
		enabled: !!actionRequiredId,
	});
	const name = retainer?.data.name_doc ?? '';
	const signDate = retainer?.data.signed_at ?? '';
	const documentUrl = retainer?.data.url_doc ?? '';
	const signature = retainer?.data.signature ?? '';

	const actionDetails: HttpResponse<SignDocumentDetails> | undefined = {
		data: {
			created_at: retainer?.data.created_at ?? '',
			file_size: retainer?.data.file_size ?? '',
			name_doc: name,
			signature: signature,
			signed_at: retainer?.data.signed_at ?? '',
			status: null,
			url_doc: documentUrl,
			is_approved: retainer?.data.status ?? false,
			is_signature_required: false,
			required_fields: [],
			review_fields: [],
			hide_signing_date: false,
			action_id: 0,
			document_id: 0,
			is_from_case_creation: false,
			version: null,
			version_history: [],
			restored: null,
			firstname: null,
			lastname: null,
			last_message: { content: null, created_at: null },
			extracted: {
				form: { fields: [], totalFields: 0, hasFormFields: false },
				text: {
					info: { numPages: 0 },
					meta: {
						Title: null,
						Author: null,
						Creator: null,
						Producer: null,
						CreationDate: null,
						PDFFormatVersion: null,
						IsAcroFormPresent: false,
					},
					pages: [],
					totalPages: 0,
				},
				vectorImages: { images: [], totalPages: 0, totalExtracted: 0 },
			},
		},
		statusCode: 200,
		message: '',
	};

	const handleCloseModal = () => {
		hide();
		setIsModalOpen(false);
	};
	return (
		<Modal
			classNameCardContainer="!bg-gradient-to-r from-white to-white md:w-full max-w-[1220px]  h-[90vh] relative"
			withXButton
			closeIconTopPosition="31px"
			title={
				<ModalHeader
					title={<ContentTitle title={name} time={signDate} />}
					classNameContent="!justify-start"
					classNameImage="!block"
				/>
			}
			onClose={handleCloseModal}
			classNameContainerChildren={
				'min-h-[65vh] !max-h-[65vh] md:!max-h-full no-scrollbar-mobile'
			}
			// footer={footer}
			classNameFooter="md:pt-6"
		>
			{isLoading ? (
				<LoaderScreen />
			) : (
				<section className="flex flex-col md:flex-row gap-6">
					<div className="md:w-[58%] xl:w-2/3 ">
						{documentUrl && (
							<PdfViewer
								showControls={true}
								pdfUrl={documentUrl}
								rightControls={
									<PdfViewerDownloadControl
										documentUrl={documentUrl}
										fileName={name}
									/>
								}
							/>
						)}
					</div>
					<div className="flex-1 space-y-4">
						<DocumentInformationSection
							actionDetails={actionDetails}
							isSignRequired={!!signature}
							documentSignature={signature}
							fields={[]}
						/>
					</div>
				</section>
			)}
		</Modal>
	);
};
